import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Settings,
  Save,
  Loader2,
  CheckCircle,
  Mail,
  BarChart3,
  Info,
  ArrowLeft,
  LogOut,
  Lock,
  Users,
  Target,
  Send,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { getAdminToken, useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => ({ Authorization: `Bearer ${getAdminToken()}` });

export default function AdminConfigPage() {
  const { user, logout } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [stats, setStats] = useState({ total_downloads: 0 });
  const [config, setConfig] = useState({
    gtm_id: "",
    mailerlite_group_id: "",
    mailerlite_api_key: "",
    meta_pixel_id: "",
    meta_access_token: "",
    meta_test_event_code: "",
  });
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [isChangingPwd, setIsChangingPwd] = useState(false);

  const handleChangePassword = async () => {
    if (!pwd.current || !pwd.next || !pwd.confirm) {
      toast.error("Rellena los 3 campos de contraseña");
      return;
    }
    if (pwd.next.length < 10) {
      toast.error("La nueva contraseña debe tener al menos 10 caracteres");
      return;
    }
    if (pwd.next !== pwd.confirm) {
      toast.error("La nueva contraseña y la confirmación no coinciden");
      return;
    }
    setIsChangingPwd(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: pwd.current, new_password: pwd.next },
        { headers: authHeaders() }
      );
      toast.success("Contraseña actualizada. Vuelve a iniciar sesión.");
      setPwd({ current: "", next: "", confirm: "" });
      setTimeout(() => logout(), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al cambiar la contraseña");
    } finally {
      setIsChangingPwd(false);
    }
  };

  useEffect(() => {
    let ignore = false;

    const loadConfig = async () => {
      try {
        const response = await axios.get(`${API}/config/admin`, { headers: authHeaders() });
        if (ignore) return;
        setConfig({
          gtm_id: response.data?.gtm_id || "",
          mailerlite_group_id: response.data?.mailerlite_group_id || "",
          mailerlite_api_key: response.data?.mailerlite_api_key || "",
          meta_pixel_id: response.data?.meta_pixel_id || "",
          meta_access_token: response.data?.meta_access_token || "",
          meta_test_event_code: response.data?.meta_test_event_code || "",
        });
      } catch (error) {
        if (!ignore) console.error("Error loading config:", error);
      } finally {
        if (!ignore) setIsLoading(false);
      }
    };

    const loadStats = async () => {
      try {
        const response = await axios.get(`${API}/stats/downloads`);
        if (!ignore) setStats(response.data);
      } catch (error) {
        if (!ignore) console.error("Error loading stats:", error);
      }
    };

    loadConfig();
    loadStats();

    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isTestingMeta, setIsTestingMeta] = useState(false);

  const handleTestMetaEvent = async () => {
    setIsTestingMeta(true);
    try {
      await axios.post(`${API}/meta/test-event`, {}, { headers: authHeaders() });
      toast.success("Evento de prueba enviado. Revísalo en Events Manager → Test Events.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Error al enviar el evento de prueba");
    } finally {
      setIsTestingMeta(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/config`, config, { headers: authHeaders() });
      toast.success("Configuración guardada correctamente");
    } catch (error) {
      console.error("Error saving config:", error);
      toast.error("Error al guardar la configuración");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-galvani-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-galvani-purple animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-galvani-bg" data-testid="admin-config-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200 py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" className="flex items-center gap-3 group">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-galvani-blue to-galvani-purple flex items-center justify-center shadow-md">
                <span className="font-heading font-extrabold text-lg text-white">G</span>
              </div>
              <span className="font-heading font-bold text-lg tracking-wider text-galvani-text hidden sm:inline">
                GALVANI
              </span>
            </a>
            <span className="text-slate-300 font-body text-sm">/</span>
            <span className="text-galvani-textLight font-body text-sm">Configuración</span>
          </div>
          <div className="flex items-center gap-4">
            {user && user !== false && (
              <span className="hidden sm:inline text-xs text-galvani-textLight font-body">
                {user.email}
              </span>
            )}
            <a
              href="/admin/leads"
              className="inline-flex items-center gap-2 text-sm text-galvani-textLight hover:text-galvani-purple font-body transition-colors"
              data-testid="admin-leads-link"
            >
              <Users size={16} />
              <span className="hidden sm:inline">CRM Leads</span>
            </a>
            <button
              onClick={() => { logout(); toast.success("Sesión cerrada"); }}
              className="inline-flex items-center gap-2 text-sm text-galvani-textLight hover:text-galvani-purple font-body transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut size={16} />
              Cerrar sesión
            </button>
            <a
              href="/"
              className="hidden sm:inline-flex items-center gap-2 text-sm text-galvani-textLight hover:text-galvani-purple font-body transition-colors"
              data-testid="admin-back-link"
            >
              <ArrowLeft size={16} />
              Volver a la web
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Title */}
            <div className="flex items-center gap-4 mb-10">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-galvani-blue to-galvani-purple flex items-center justify-center shadow-lg shadow-blue-500/20">
                <Settings className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-heading font-extrabold text-3xl sm:text-4xl text-galvani-text">
                  Panel de <span className="gradient-text">Configuración</span>
                </h1>
                <p className="font-body text-sm text-galvani-textLight mt-1">
                  Gestiona integraciones y consulta las métricas de la web.
                </p>
              </div>
            </div>

            {/* Stats Card */}
            <div className="mb-8 p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3 mb-5">
                <BarChart3 className="w-5 h-5 text-galvani-blue" />
                <h2 className="font-heading font-bold text-lg text-galvani-text">
                  Estadísticas
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-5 rounded-xl bg-gradient-to-br from-blue-50 to-purple-50 border border-purple-100">
                  <p className="font-body text-xs text-galvani-textLight mb-1 uppercase tracking-wider">
                    Descargas de guía
                  </p>
                  <p
                    className="font-heading font-extrabold text-3xl bg-gradient-to-r from-galvani-blue to-galvani-purple bg-clip-text text-transparent"
                    data-testid="stats-downloads"
                  >
                    {stats.total_downloads}
                  </p>
                </div>
              </div>
            </div>

            {/* Google Tag Manager */}
            <div className="mb-8 p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3 mb-5">
                <BarChart3 className="w-5 h-5 text-galvani-purple" />
                <h2 className="font-heading font-bold text-lg text-galvani-text">
                  Google Tag Manager
                </h2>
              </div>

              <div className="mb-5 p-4 rounded-xl bg-blue-50 border border-blue-200">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-body text-sm text-blue-900 mb-2 font-semibold">
                      ¿Cómo obtener el ID de GTM?
                    </p>
                    <ol className="font-body text-xs text-blue-800 space-y-1 list-decimal list-inside">
                      <li>
                        Ve a{" "}
                        <a
                          href="https://tagmanager.google.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline hover:text-blue-600 font-medium"
                        >
                          tagmanager.google.com
                        </a>
                      </li>
                      <li>Crea o selecciona tu contenedor</li>
                      <li>
                        Copia el ID que empieza por{" "}
                        <code className="bg-blue-200 text-blue-900 px-1.5 py-0.5 rounded text-xs font-mono">
                          GTM-XXXXXXX
                        </code>
                      </li>
                    </ol>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-galvani-text text-sm mb-2 block font-body font-medium">
                  ID de Google Tag Manager
                </Label>
                <Input
                  placeholder="GTM-XXXXXXX"
                  value={config.gtm_id}
                  onChange={(e) =>
                    setConfig((prev) => ({ ...prev, gtm_id: e.target.value }))
                  }
                  className="h-12 rounded-xl font-mono text-sm"
                  data-testid="gtm-id-input"
                />
                <p className="mt-2 text-xs text-galvani-textLight font-body">
                  El código se inyectará automáticamente en todas las páginas cuando el usuario acepte las cookies de análisis.
                </p>
              </div>
            </div>

            {/* Mailerlite */}
            <div className="mb-8 p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3 mb-5">
                <Mail className="w-5 h-5 text-galvani-pink" />
                <h2 className="font-heading font-bold text-lg text-galvani-text">
                  MailerLite
                </h2>
              </div>

              <div className="mb-5 p-4 rounded-xl bg-green-50 border border-green-200">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-body text-sm text-green-900 mb-2 font-semibold">
                      Configuración de MailerLite
                    </p>
                    <ol className="font-body text-xs text-green-800 space-y-1 list-decimal list-inside">
                      <li>
                        Ve a{" "}
                        <a
                          href="https://dashboard.mailerlite.com/integrations/api"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline hover:text-green-600 font-medium"
                        >
                          MailerLite → Integrations → API
                        </a>
                      </li>
                      <li>Genera o copia tu API Key</li>
                      <li>Ve a Subscribers → Groups y copia el ID del grupo donde quieres añadir los leads</li>
                      <li>Crea una automatización en MailerLite para enviar la guía cuando un suscriptor se una al grupo</li>
                    </ol>
                  </div>
                </div>
              </div>

              <div className="space-y-5">
                <div>
                  <Label className="text-galvani-text text-sm mb-2 block font-body font-medium">
                    API Key de MailerLite
                  </Label>
                  <Input
                    type="password"
                    placeholder="eyJ0eXAiOiJKV1QiLCJhbGci..."
                    value={config.mailerlite_api_key}
                    onChange={(e) =>
                      setConfig((prev) => ({ ...prev, mailerlite_api_key: e.target.value }))
                    }
                    className="h-12 rounded-xl font-mono text-sm"
                    data-testid="mailerlite-api-key-input"
                  />
                </div>

                <div>
                  <Label className="text-galvani-text text-sm mb-2 block font-body font-medium">
                    ID del Grupo de MailerLite
                  </Label>
                  <Input
                    placeholder="123456789"
                    value={config.mailerlite_group_id}
                    onChange={(e) =>
                      setConfig((prev) => ({ ...prev, mailerlite_group_id: e.target.value }))
                    }
                    className="h-12 rounded-xl font-mono text-sm"
                    data-testid="mailerlite-group-id-input"
                  />
                  <p className="mt-2 text-xs text-galvani-textLight font-body">
                    Los leads de la guía se añadirán automáticamente a este grupo.
                  </p>
                </div>
              </div>
            </div>

            {/* Meta / Facebook */}
            <div className="mb-8 p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3 mb-5">
                <Target className="w-5 h-5 text-galvani-blue" />
                <h2 className="font-heading font-bold text-lg text-galvani-text">
                  Meta / Facebook Ads
                </h2>
              </div>

              <div className="mb-5 p-4 rounded-xl bg-indigo-50 border border-indigo-200">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-body text-sm text-indigo-900 mb-2 font-semibold">
                      Pixel + Conversions API (server-side)
                    </p>
                    <ol className="font-body text-xs text-indigo-800 space-y-1 list-decimal list-inside">
                      <li>
                        Ve a{" "}
                        <a
                          href="https://business.facebook.com/events_manager2"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline hover:text-indigo-600 font-medium"
                        >
                          Meta Events Manager
                        </a>{" "}
                        → Orígenes de datos → tu Píxel
                      </li>
                      <li>Copia el <strong>Pixel ID</strong> (15-16 dígitos)</li>
                      <li>
                        Configuración → Conversions API → <strong>Generar token de acceso</strong> y pégalo abajo
                      </li>
                      <li>
                        Opcional: en "Probar eventos" copia el <strong>Test Event Code</strong> para validar. Bórralo al terminar.
                      </li>
                    </ol>
                    <p className="font-body text-xs text-indigo-900 mt-2">
                      <strong>Importante:</strong> si ya tienes el Píxel de Meta dentro de Google Tag Manager, no pongas el Pixel ID aquí (se cargaría dos veces). Deja solo el Access Token para la Conversions API.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-5">
                <div>
                  <Label className="text-galvani-text text-sm mb-2 block font-body font-medium">
                    Pixel ID
                  </Label>
                  <Input
                    placeholder="123456789012345"
                    value={config.meta_pixel_id}
                    onChange={(e) =>
                      setConfig((prev) => ({ ...prev, meta_pixel_id: e.target.value }))
                    }
                    className="h-12 rounded-xl font-mono text-sm"
                    data-testid="meta-pixel-id-input"
                  />
                  <p className="mt-2 text-xs text-galvani-textLight font-body">
                    El Pixel se cargará en la web solo cuando el visitante acepte las cookies de marketing.
                  </p>
                </div>

                <div>
                  <Label className="text-galvani-text text-sm mb-2 block font-body font-medium">
                    Access Token (Conversions API)
                  </Label>
                  <Input
                    type="password"
                    placeholder="EAAG..."
                    value={config.meta_access_token}
                    onChange={(e) =>
                      setConfig((prev) => ({ ...prev, meta_access_token: e.target.value }))
                    }
                    className="h-12 rounded-xl font-mono text-sm"
                    data-testid="meta-access-token-input"
                  />
                  <p className="mt-2 text-xs text-galvani-textLight font-body">
                    Se guarda solo en el servidor. Nunca se expone al navegador.
                  </p>
                </div>

                <div>
                  <Label className="text-galvani-text text-sm mb-2 block font-body font-medium">
                    Test Event Code (opcional)
                  </Label>
                  <Input
                    placeholder="TEST12345"
                    value={config.meta_test_event_code}
                    onChange={(e) =>
                      setConfig((prev) => ({ ...prev, meta_test_event_code: e.target.value }))
                    }
                    className="h-12 rounded-xl font-mono text-sm"
                    data-testid="meta-test-event-code-input"
                  />
                </div>

                <Button
                  onClick={handleTestMetaEvent}
                  disabled={isTestingMeta}
                  variant="outline"
                  className="border-galvani-blue text-galvani-blue hover:bg-blue-50 font-body font-semibold rounded-full h-11 px-6"
                  data-testid="meta-test-event-btn"
                >
                  {isTestingMeta ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Enviar evento de prueba a Meta
                    </>
                  )}
                </Button>
                <p className="text-xs text-galvani-textLight font-body">
                  Guarda primero la configuración. El evento aparecerá en Events Manager → Probar eventos.
                </p>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                onClick={handleSave}
                disabled={isSaving}
                className="bg-gradient-to-r from-galvani-blue to-galvani-purple hover:opacity-90 text-white font-body font-semibold px-8 h-12 rounded-full transition-all duration-300 hover:scale-[1.02] shadow-lg shadow-blue-500/25"
                data-testid="save-config-btn"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    Guardar configuración
                  </>
                )}
              </Button>
            </div>

            {/* Help Section */}
            <div className="mt-12 p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-purple-50 border border-purple-100">
              <h3 className="font-heading font-bold text-lg text-galvani-text mb-4">
                ¿Cómo funciona?
              </h3>
              <div className="space-y-4 font-body text-sm text-galvani-textLight">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p>
                    <strong className="text-galvani-text">Google Tag Manager:</strong> Una vez configurado, el código GTM se cargará automáticamente en todas las páginas cuando el visitante acepte las cookies de análisis. Podrás trackear conversiones, eventos y conectar con Google Analytics, Meta Pixel, etc.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p>
                    <strong className="text-galvani-text">MailerLite:</strong> Cuando alguien descargue la guía, se añadirá automáticamente al grupo que especifiques. Configura una automatización en MailerLite para enviar el PDF de la guía.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p>
                    <strong className="text-galvani-text">Meta:</strong> Cada envío del formulario dispara un evento <code>Lead</code> desde el servidor (Conversions API) con email y teléfono hasheados, y otro desde el navegador con el mismo <code>event_id</code>, así Meta deduplica y no cuenta el lead dos veces. Solo se envía si el visitante acepta las cookies de marketing.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
